import {AbstractControl, UntypedFormControl, UntypedFormGroup, FormGroupDirective, NgForm, ValidationErrors, ValidatorFn, Validators, FormGroup} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import {isValidPhoneNumber} from 'libphonenumber-js';
import moment from 'moment';

import {TerraNumberPipe} from '../pipes/TerraNumber.pipe';

function isEmptyInputValue(value: any): boolean {
  // we don't check for string here so it also works with arrays
  return value == null || value.length === 0;
}

function customRulesValidation(fullPhone: string): boolean {
  const phoneParts = fullPhone.split(' ');
  if (phoneParts?.length === 2) {
    const prefix = phoneParts[0].replace('+', '');
    const body = phoneParts[1];

    // Rule #1: US numbers shouldn't start with '1' (double prefix)
    if (prefix === '1' && body.startsWith(prefix)) {
      return false;
    }
    // Rule #2:

    return true;
  }
}

export class CustomValidators {
  static EmailWithSpaces(formControl: UntypedFormControl) {
    const regexEmail = /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    if (isEmptyInputValue(formControl.value)) {
      return null;  // don't validate empty values to allow optional controls
    }

    // If email is valid (ignore leading/trailing whitespace)
    if (regexEmail.test(formControl.value.trim())) {
      // Remove leading/trailing whitespace if needed
      if (formControl.value !== formControl.value.trim()) {
        formControl.patchValue(formControl.value.trim(), {emitEvent: false, onlySelf: true, emitModelToViewChange: false});
      }
      return null;
    } else {
      return {email: true};
    }
  }

  /** @see https://voximplant.com/blog/javascript-regular-expression-for-phone-number-verification */
  static InternationalPhoneNumber(formControl: UntypedFormControl) {
    if (isEmptyInputValue(formControl.value)) {
      return null;  // don't validate empty values to allow optional controls
    }

    if (!customRulesValidation(formControl.value.trim())) {
      return {phone: true};
    }

    const isValid = isValidPhoneNumber(formControl.value.trim());

    if (!isValid) {
      return {phone: true};
    } else {
      return null;
    }
  }

  /**
   * Validates if at least one of the provided fields has a value.
   * Fields can only be of type number or string.
   * @param fields name of the form fields that should be checked
   */
  static atLeastOne(...fields: string[]) {
    return (fg: UntypedFormGroup): ValidationErrors | null => {
      return fields.some(fieldName => {
        const field = fg.get(fieldName).value;
        if (typeof field === 'number') {
          return field && field >= 0 ? true : false;
        }
        if (typeof field === 'string') {
          return field && field.length > 0 ? true : false;
        }
      })
        ? null
        : ({atLeastOne: 'At least one field has to be provided.'} as ValidationErrors);
    };
  }

  /**
   * @description
   * Makes the field required if the predicate function returns true
   * @see https://medium.com/ngx/3-ways-to-implement-conditional-validation-of-reactive-forms-c59ed6fc3325
   * @usageNotes
   *
   * ### Validate that the field is non-empty IF another field has a value
   *
   * ```typescript
   *
   * this.myForm = this.fb.group({
   *  myCheckbox: [''],
   *  myEmailField: ['', [
   *  Validators.minLength(5),
   *  Validators.pattern(/.+@.+\..+/),
   *  // <--- here we pass predicate arrow function
   *  requiredIfValidator(() => this.myForm.get('myCheckbox').value)
   *  ]]
   * })
   *
   * ```
   *
   * @returns An error map with the `required` property
   * if the validation check fails, otherwise `null`.
   */
  static requiredIf(predicate): ValidatorFn {
    return (formControl => {
      if (!formControl.parent) {
        return null;
      }
      if (predicate()) {
        return Validators.required(formControl);
      }
      return null;
    });
  }

  static confirmedValidator(matchingControl: UntypedFormControl) {
    return control => {
      if (matchingControl.errors && !matchingControl.errors.confirmedValidator) {
        return null;
      }
      if (!control.value && !matchingControl.value) {
        return null;
      }
      if (control.value !== matchingControl.value) {
        return { confirmedValidator: true };
      } else {
        return null;
      }
    };
  }

  static patternIf(predicate, pattern): ValidatorFn {
    return (formControl => {
      if (!formControl.parent) {
        return null;
      }
      if (predicate()) {
        return RegExp(pattern).test(formControl.value) ? null : ({pattern: pattern});
      }
      return null;
    });
  }

  static maxFormattedNumber(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const numberPipe = new TerraNumberPipe(null);
      const parsedNumber = numberPipe.parse(value);

      return parsedNumber > max ? {maxFormattedNumber: true} : null;
    };
  }
  static secondaryMaxFormattedNumber(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const numberPipe = new TerraNumberPipe(null);
      const parsedNumber = numberPipe.parse(value);

      return parsedNumber > max ? {secondaryMaxFormattedNumber: true} : null;
    };
  }

  static minFormattedNumber(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const numberPipe = new TerraNumberPipe(null);
      const parsedNumber = numberPipe.parse(value);

      return parsedNumber <= min ? {minFormattedNumber: true} : null;
    };
  }

  static containsForbiddenCharacterValidator(illegalCharacters: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value;
      if (!value) {
        return null;
      }

      return illegalCharacters.some(char => value.indexOf(char) !== -1) ? {illegalCharacter: true} : null;
    };
  }

  static beforeOrAfterDateValidation(date: Date, state: 'isBeforeEqual' | 'isAfterEqual'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const invalid = {invalidDate: true};
      const value: Date = control.value;
      if (!value || !date) {
        return invalid;
      }
      const valueDate = moment(value).startOf('day').utc();
      const paramDate = moment(date).startOf('day').utc();
      switch (state) {
        case 'isBeforeEqual':
          if (!valueDate.isAfter(paramDate)) {
            return null;
          }
          break;
        case 'isAfterEqual':
          if (!valueDate.isBefore(paramDate)) {
            return null;
          }
          break;
        default:
          break;
      }
      return invalid;
    };
  }

  /**
   * Validates that a value is not part of a list
   * @param list list of values
   */
  static existInListValidation(list: any[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control || !list) {
        return null;
      }

      return list.includes(control.value) ? {exist: true} : null;
    };
  }


  /**
   * Validate if the current value is changed by compare current value with value that send as parameter.
   * @param valueToCompare value that save
   * @param typeOfValue type of value
   * @param terraNumber Terra Number Pipe
   * @returns
   */
  static isValueChangedValidation(valueToCompare: number | string | Date, typeOfValue: "number" | "string" | "Date", terraNumber: TerraNumberPipe = null): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isInValid = {hasChange: true};
      switch (typeOfValue) {
        case "Date":
          const callDateMoment = moment(valueToCompare).utc();
          const fieldDateMoment = moment(control.value).utc();
          if (!callDateMoment.isSame(fieldDateMoment)) {
            return isInValid;
          }
          return null;

        case "number":
          if (!!terraNumber && terraNumber.transform(`${control.value}`) !== terraNumber.transform(`${valueToCompare}`)) {
            return isInValid;
          } else if (!terraNumber && `${control.value}` !== `${valueToCompare}`) {
            return isInValid;
          }
          return null;

        case "string":
          if (control.value !== valueToCompare) {
            return isInValid;
          }
          return null;

        default:
          return isInValid;
      }
    };
  }

  /**
   * Checking whether language map is Hebrew
   */
  static HebrewForbiddenIf(predicate): ValidatorFn {
    const hebRange: RegExp = /[\u0590-\u05FF]/; // Unicode Hebrew range

    return (formControl => {
      if (!formControl.parent) {
        return null;
      }
      if (predicate()) {
        return hebRange.test(formControl.value) ? {hebrewForbidden: true} : null;
      }
      return null;
    });
  }
}

export class AtLeastOneStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const invalidCtrl = control && control.invalid && control.parent.touched;

    // parent has passwordMatch error
    const invalidParent = control && control.parent && control.parent.hasError('atLeastOne');

    return (control.touched && (invalidCtrl || invalidParent));
  }
}
